import React, { useContext } from 'react'
import { ListGroup, Badge } from 'react-bootstrap'
import { useCreateDashboardVendor } from 'api/hooks/use-dashboard-vendors'
import VendorAttribute from 'shared/badges/vendor-attribute'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClone } from '@fortawesome/free-regular-svg-icons'
import { useQueryClient } from 'react-query'
import UserContext from 'context/user-context'

function VendorItem({
  vendor,
  params,
  dashboardId,
  addOrRemoveVendor,
  updateVendorWithDashboardId,
  allVendors,
  canEdit = true
}) {
  const queryClient = useQueryClient()
  const { municipality } = useContext(UserContext)
  const queryParams = {
    vendor_id: vendor.id,
    dashboard_id: params.dashboardId,
    options: vendor.options
  }
  const { mutate } = useCreateDashboardVendor({
    params: queryParams,
    options: {
      onSuccess: (data) => {
        // Add vendor to local state with the dashboard_vendor_id from the response
        const vendorWithDashboardId = { ...vendor, dashboard_vendor_id: data.id }
        addOrRemoveVendor(vendorWithDashboardId, data.id)
        queryClient.invalidateQueries(`dashboards/${dashboardId}`)
      }
    }
  })

  const isAlreadySelected =
    allVendors && allVendors.some((v) => v.id === vendor.id)

  return (
    <ListGroup.Item
      action
      key={`vendor-option-${vendor.id}`}
      active={isAlreadySelected ? true : false}
      className={isAlreadySelected ? 'py-2 bg-light' : 'py-2'}
      onClick={() => {
        if (!canEdit) return
        if (!isAlreadySelected) {
          // If dashboard doesn't exist yet, stage vendor; otherwise, create via API
          if (!params.dashboardId) {
            addOrRemoveVendor(vendor)
          } else {
            mutate()
          }
        } else {
          addOrRemoveVendor(vendor)
        }
      }}
      style={!canEdit ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
    >
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <span className="text-primary">{vendor.name} </span>
          <small className="text-muted">(ID: {vendor.external_id})</small>
          {vendor.status && (
            <span className="text-muted ms-2">{vendor.status}</span>
          )}
        </div>
        {isAlreadySelected && (
          <Badge className="ms-2 bg-white text-primary border-1 border-primary">
            Selected
          </Badge>
        )}
      </div>
      <div className="mt-1">
        {vendor.recent_payment_change && (
          <VendorAttribute
            title="Recent Payment Change"
            tooltipText={`Vendor payment method changed in the last 30 days: ${vendor.last_payment_method_change_on}`}
          />
        )}
        {vendor.stale && (
          <VendorAttribute
            title="Stale"
            tooltipText={`Vendor has not sent invoice or received a check in ${municipality.stale_vendor_months} months. Last Invoice: ${vendor.last_invoice_on} Last Check: ${vendor.last_check_on}`}
          />
        )}
        {vendor.po_box && (
          <VendorAttribute
            title="PO Box"
            tooltipText={'Vendor has a PO Box address'}
          />
        )}
        {vendor.one_eight_hundred && (
          <VendorAttribute
            title="1-800"
            tooltipText={'Vendor has an 800 number'}
          />
        )}
        {vendor.is_duplicate_ein && (
          <VendorAttribute
            title={
              <span>
                <FontAwesomeIcon icon={faClone} /> EIN
              </span>
            }
            tooltipText={`Vendor has a duplicated EIN with another active vendor: ${vendor.duplicate_ein_vendors
              .split('||')
              .join(', ')}`}
          />
        )}
        {vendor.is_duplicate_ein_being_paid && (
          <VendorAttribute
            title={
              <span>
                <FontAwesomeIcon icon={faClone} /> EIN $
              </span>
            }
            tooltipText={`Multiple Vendors with the same EIN have been paid in the past year: ${vendor.duplicate_ein_being_paid_vendors
              .split('||')
              .join(', ')}`}
          />
        )}
      </div>
    </ListGroup.Item>
  )
}

export default VendorItem
